'use client';

import { useAtomValue } from 'jotai';

import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import type {
  CampaignOfferProps,
  OfferModalProps,
} from '@core/Entities/OfferPromoContent/OfferPromoContent.entity';
import { isEmptyRichText } from '@core/Utils/Html/IsEmptyRichText';
import { isD2cModelVsp } from '@core/Utils/Vehicle/isD2cModelVsp';
import { Typography } from '@gds/Typography/Typography';

import { getColors } from './ColorUtils';
import { renderDescription, renderRichText } from './RenderModalContent';

import styles from './OfferModal.module.css';

interface OfferModalComponentProps {
  offerModalContent: OfferModalProps;
  campaignOffers: CampaignOfferProps[];
}

export const OfferModal = ({ offerModalContent, campaignOffers }: OfferModalComponentProps) => {
  const {
    text,
    title,
    titleInvestor,
    titleEcomtwo,
    description,
    colourTitle,
    colourTitletwo,
    descriptionInvestor,
    descriptionEcomtwo,
    descriptionInvestortwo,
    titleInvestortwo,
    colorDescription,
    colorDescriptiontwo,
  } = offerModalContent;

  const [color, colorTwo, colorThree, colorFour] = getColors(
    colourTitle,
    colorDescription,
    colourTitletwo,
    colorDescriptiontwo,
  );
  const vehicle = useAtomValue(currentVehicleAtom);
  const isEcom = vehicle?.ecom.isEcom;
  const isD2C = isD2cModelVsp(vehicle);
  const isEcomAndNotD2C = !!(isEcom && !isD2C);

  return (
    <>
      <div className={styles.campaignHeader}>{title}</div>

      <div className={styles.campaignTitle} data-color={color}>
        <Typography variant="h3" Component="p">
          {renderRichText(isEcomAndNotD2C, text, titleInvestor)}
        </Typography>
      </div>
      <div className={styles.campaignDescription} data-color={colorTwo}>
        {renderDescription(isEcomAndNotD2C, description, descriptionInvestor)}
      </div>

      <div>
        <div className={styles.campaignTitle} data-color={colorThree}>
          <Typography variant="h3" Component="p">
            {renderRichText(isEcomAndNotD2C, titleEcomtwo, titleInvestortwo)}
          </Typography>
        </div>
        <div className={styles.campaignDescription} data-color={colorFour}>
          {renderDescription(isEcomAndNotD2C, descriptionEcomtwo, descriptionInvestortwo)}
        </div>

        {/* Campaign Offers */}
        {campaignOffers.length > 0 && (
          <div className={styles.campaignOffers}>
            {campaignOffers.map((offer, index) => {
              const [offerColorOne, offerColorTwo, offerColorThree, offerColorFour] = getColors(
                offer.colorCampaignone,
                offer.colorCampaigntwo,
                offer.colorCampaignthree,
                offer.colorCampaignfour,
              );
              return (
                <div key={index} className={styles.campaignOffer}>
                  <div className={styles.campaignTitle} data-color={offerColorOne}>
                    {renderRichText(true, offer.titleCampaignprimary, '')}
                  </div>
                  <div
                    className={styles.campaignDescription}
                    data-color={offerColorTwo}
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    dangerouslySetInnerHTML={{ __html: offer.descriptionCampaignprimary || '' }}
                  />
                  {!isEmptyRichText(offer.descriptionCampaignsecondary) && (
                    <>
                      <div className={styles.campaignTitle} data-color={offerColorThree}>
                        {!isEmptyRichText(offer.titleCampaignsecondary) &&
                          renderRichText(true, offer.titleCampaignsecondary, '')}
                      </div>
                      <div
                        className={styles.campaignDescription}
                        data-color={offerColorFour}
                        dangerouslySetInnerHTML={{
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          __html: offer.descriptionCampaignsecondary || '',
                        }}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
